import * as React from "react"

import { cn } from "@/lib/utils"

import {
  HOW_LABEL,
  OUTCOME_LABEL,
  OUTCOME_TONE,
  type EvalFinding,
  type EvalMark,
  type MarkOutcome,
} from "../eval-runs-data"
import { scopeSourceShort } from "../scope"
import { ScopeBadge } from "../scope-controls"

import { ScopeEditor, ScopeProvenance } from "./mark-scope"

import type { Selection } from "./selection"

type Filter = "all" | MarkOutcome

const FILTERS: readonly { key: Filter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "missed", label: "Missed" },
  { key: "caught", label: "Caught" },
  { key: "escalated", label: "Escalated" },
  { key: "not_checked", label: "Not checked" },
]

/** The marks and findings of a label, filterable by outcome; choosing one selects its box. */
export function ItemPanel(props: {
  marks: readonly EvalMark[]
  /** Out-of-scope marks left out of `marks` while the exclusion is on. */
  hiddenCount?: number
  findings: readonly EvalFinding[]
  selection: Selection
  onSelect: (s: Selection) => void
}) {
  const { marks, findings, selection } = props
  const [tab, setTab] = React.useState<"marks" | "findings">("marks")
  const [filter, setFilter] = React.useState<Filter>("all")
  const shown = marks.filter((m) => filter === "all" || m.outcome === filter)

  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-1 rounded-md border border-border p-1" role="tablist">
        {(["marks", "findings"] as const).map((t) => (
          <button
            type="button"
            key={t}
            role="tab"
            aria-selected={tab === t}
            className={cn(
              "flex-1 rounded px-2 py-1 text-body-sm",
              tab === t
                ? "bg-brand font-semibold text-white"
                : "text-text-2 hover:bg-surface-sunken",
            )}
            onClick={() => {
              setTab(t)
            }}
          >
            {t === "marks"
              ? `Expert marks (${String(marks.length)})`
              : `Our findings (${String(findings.length)})`}
          </button>
        ))}
      </div>

      {tab === "marks" && (
        <div className="flex flex-wrap gap-1" role="group" aria-label="Filter marks by outcome">
          {FILTERS.map((f) => (
            <button
              type="button"
              key={f.key}
              aria-pressed={filter === f.key}
              className={cn(
                "rounded-full border px-2 py-0.5 text-caption",
                filter === f.key
                  ? "border-brand bg-brand-tint text-text"
                  : "border-border text-text-2 hover:bg-surface-sunken",
              )}
              onClick={() => {
                setFilter(f.key)
              }}
            >
              {f.label}
              {f.key !== "all" && ` (${String(marks.filter((m) => m.outcome === f.key).length)})`}
            </button>
          ))}
        </div>
      )}

      {tab === "marks" && (props.hiddenCount ?? 0) > 0 && (
        <p className="px-1 text-caption text-text-3">
          {props.hiddenCount} out-of-scope {props.hiddenCount === 1 ? "mark" : "marks"} hidden
        </p>
      )}

      <div className="max-h-[36vh] overflow-y-auto rounded-md border border-border bg-surface">
        {tab === "marks" && shown.length === 0 && (
          <p className="p-3 text-body-sm text-text-3">No mark has this outcome.</p>
        )}
        {tab === "marks" &&
          shown.map((m) => (
            <button
              type="button"
              key={m.id}
              onClick={() => {
                props.onSelect({ kind: "mark", id: m.id })
              }}
              className={cn(
                "flex w-full items-start gap-2 border-b border-border-soft px-3 py-2 text-left last:border-0",
                selection?.kind === "mark" && selection.id === m.id
                  ? "bg-brand-tint"
                  : "hover:bg-surface-sunken",
              )}
            >
              <span
                aria-hidden
                className="mt-1.5 size-2.5 shrink-0 rounded-full"
                style={{ background: OUTCOME_TONE[m.outcome] }}
              />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-body-sm text-text">
                  {m.comment || m.highlighted || "(no comment)"}
                </span>
                <span className="flex flex-wrap items-center gap-x-1.5 text-caption text-text-3">
                  <span>
                    {m.id} · p{m.page + 1} · {OUTCOME_LABEL[m.outcome]} ·{" "}
                    {m.rules.join(", ") || "no rule"}
                  </span>
                  <ScopeBadge scope={m.scope} reason={m.scopeReason} />
                  {scopeSourceShort(m) && <span>{scopeSourceShort(m)}</span>}
                </span>
              </span>
            </button>
          ))}
        {tab === "findings" &&
          findings.map((f) => (
            <button
              type="button"
              key={f.id}
              onClick={() => {
                props.onSelect({ kind: "finding", id: f.id })
              }}
              className={cn(
                "flex w-full flex-col border-b border-border-soft px-3 py-2 text-left last:border-0",
                selection?.kind === "finding" && selection.id === f.id
                  ? "bg-brand-tint"
                  : "hover:bg-surface-sunken",
              )}
            >
              <span className="truncate text-body-sm text-text">{f.message}</span>
              <span className="text-caption text-text-3">
                {f.ruleId} · {f.output === "needs_sme" ? "hand-off" : f.output}
                {f.boxes[0] ? ` · p${String(f.boxes[0].page + 1)}` : " · whole document"}
              </span>
            </button>
          ))}
      </div>
    </div>
  )
}

/** What the selected mark is, and how the review answered it. */
export function MarkDetail(props: {
  mark: EvalMark
  finding: EvalFinding | undefined
  onOpenFinding: (s: Selection) => void
  runId: number
  /** How the run's marks were tagged, named beside an AI call. */
  scopeMethod: string | null
  /** Whether the viewer may decide the mark's scope (admins). */
  canEditScope: boolean
}) {
  const { mark, finding } = props
  const answered = mark.outcome === "caught" || mark.outcome === "escalated"
  return (
    <div className="rounded-md border border-border bg-surface p-3 text-body-sm text-text">
      <div className="mb-1 flex items-center gap-2">
        <span
          className="rounded-full px-2 py-0.5 text-caption font-semibold text-white"
          style={{ background: OUTCOME_TONE[mark.outcome] }}
        >
          {OUTCOME_LABEL[mark.outcome]}
        </span>
        <span className="text-caption text-text-3">
          {mark.id} · page {mark.page + 1}
        </span>
        <ScopeBadge scope={mark.scope} reason={mark.scopeReason} />
      </div>
      {mark.scopeSource === "default" && mark.scope !== "in_scope" && mark.scopeReason && (
        <p className="mb-1 text-caption text-text-3">Out of scope: {mark.scopeReason}</p>
      )}
      <ScopeProvenance mark={mark} method={props.scopeMethod} />
      <p className="whitespace-pre-wrap">{mark.comment}</p>
      {mark.highlighted && (
        <p className="mt-1 text-caption text-text-3">Marked text: “{mark.highlighted}”</p>
      )}
      {finding && answered && (
        <p className="mt-2 text-caption">
          Answered by{" "}
          <button
            type="button"
            className="text-brand underline"
            onClick={() => {
              props.onOpenFinding({ kind: "finding", id: finding.id })
            }}
          >
            {finding.ruleId}: {finding.message}
          </button>
          {mark.how ? ` (${HOW_LABEL[mark.how]})` : ""}
        </p>
      )}
      {mark.rejectedByTiebreak && (
        <p className="mt-2 text-caption text-text-3">
          A finding on the same page was considered and judged a different problem
          {mark.tiebreakReason ? `: ${mark.tiebreakReason}` : "."}
        </p>
      )}
      {props.canEditScope && <ScopeEditor key={mark.id} runId={props.runId} mark={mark} />}
    </div>
  )
}
